export type Point = { x: number; y: number };

export type ViewportState = {
  panX: number;
  panY: number;
  zoom: number;
};

export type Size = { width: number; height: number };

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 4;
export const ZOOM_STEP = 0.1;

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) {
    return MIN_ZOOM;
  }
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

export function zoomToPercent(zoom: number): number {
  return Math.round(zoom * 100);
}

export function percentToZoom(percent: number): number {
  return clampZoom(percent / 100);
}

export function parseZoomPercentInput(value: string): number | null {
  const trimmed = value.trim().replace(/%/g, '');
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return percentToZoom(parsed);
}

/** Step to the next 10% increment. Fit zoom below 10% is preserved on zoom-out. */
export function stepZoom(zoom: number, direction: 1 | -1): number {
  if (direction === 1) {
    return clampZoom(Math.floor(zoom * 10 + 1e-6) / 10 + ZOOM_STEP);
  }
  if (zoom < MIN_ZOOM) {
    return zoom;
  }
  return clampZoom(Math.ceil(zoom * 10 - 1e-6) / 10 - ZOOM_STEP);
}

export function viewportCenter(stage: Size): Point {
  return { x: stage.width / 2, y: stage.height / 2 };
}

/** Fit template frame into viewport; pan/zoom are view-only session state. */
export function fitFrameToViewport(frame: Size, viewport: Size, padding = 0): ViewportState {
  const innerWidth = Math.max(1, viewport.width - padding * 2);
  const innerHeight = Math.max(1, viewport.height - padding * 2);
  const zoom = Math.min(innerWidth / frame.width, innerHeight / frame.height);
  const panX = padding + (innerWidth - frame.width * zoom) / 2;
  const panY = padding + (innerHeight - frame.height * zoom) / 2;

  return { panX, panY, zoom };
}

export function screenToStage(point: Point, viewport: ViewportState): Point {
  return {
    x: (point.x - viewport.panX) / viewport.zoom,
    y: (point.y - viewport.panY) / viewport.zoom,
  };
}

export function stageToScreen(point: Point, viewport: ViewportState): Point {
  return {
    x: point.x * viewport.zoom + viewport.panX,
    y: point.y * viewport.zoom + viewport.panY,
  };
}

export function zoomAtPoint(
  viewport: ViewportState,
  screenPoint: Point,
  nextZoom: number,
): ViewportState {
  const zoom = clampZoom(nextZoom);
  const stageAnchor = screenToStage(screenPoint, viewport);
  return {
    zoom,
    panX: screenPoint.x - stageAnchor.x * zoom,
    panY: screenPoint.y - stageAnchor.y * zoom,
  };
}

export function zoomAtViewportCenter(
  viewport: ViewportState,
  stage: Size,
  nextZoom: number,
): ViewportState {
  return zoomAtPoint(viewport, viewportCenter(stage), nextZoom);
}

export function panBy(viewport: ViewportState, screenDelta: Point): ViewportState {
  return {
    ...viewport,
    panX: viewport.panX + screenDelta.x,
    panY: viewport.panY + screenDelta.y,
  };
}

/** Pointer movement in screen space → document/template delta (independent of pan). */
export function screenDeltaToStageDelta(screenDelta: Point, zoom: number): Point {
  return { x: screenDelta.x / zoom, y: screenDelta.y / zoom };
}

/** Refit after viewport resize; document coordinates unchanged. */
export function refitViewport(frame: Size, nextViewport: Size, padding = 0): ViewportState {
  return fitFrameToViewport(frame, nextViewport, padding);
}

/** Keep zoom; re-center frame when the stage container resizes. */
export function recenterViewportPreservingZoom(
  frame: Size,
  stage: Size,
  zoom: number,
  padding = 0,
): ViewportState {
  const innerWidth = Math.max(1, stage.width - padding * 2);
  const innerHeight = Math.max(1, stage.height - padding * 2);
  return {
    zoom,
    panX: padding + (innerWidth - frame.width * zoom) / 2,
    panY: padding + (innerHeight - frame.height * zoom) / 2,
  };
}

function isUsableStageSize(stage: Size): boolean {
  return stage.width > 0 && stage.height > 0;
}

/**
 * Fit the first time the stage has a real size (fill-container measure).
 * Later resizes keep the current zoom so a window change does not jump scale.
 */
export function viewportAfterStageSizeChange(
  previousStage: Size,
  nextStage: Size,
  frame: Size,
  currentZoom: number,
  padding = 0,
): ViewportState | null {
  if (!isUsableStageSize(nextStage)) {
    return null;
  }
  if (!isUsableStageSize(previousStage)) {
    return fitFrameToViewport(frame, nextStage, padding);
  }
  return recenterViewportPreservingZoom(frame, nextStage, currentZoom, padding);
}
